import React, { useState, useEffect } from "react";
import axios from "axios";

function NewsletterTab() {
  const [subscribers, setSubscribers] = useState([]);
  const [newSubscriber, setNewSubscriber] = useState({ email: "" });
  const [editingSubscriber, setEditingSubscriber] = useState(null);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  useEffect(() => {
    fetchSubscribers();
  }, []);

  const fetchSubscribers = async () => {
    try {
      setLoading(true);
      const response = await axios.get("/api/newsletter", {
        withCredentials: true,
      });
      setSubscribers(response.data);
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to fetch subscribers");
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleChange = (e) => {
    const { value } = e.target;

    if (editingSubscriber) {
      setEditingSubscriber({ ...editingSubscriber, email: value });
    } else {
      setNewSubscriber({ email: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const emailToValidate = editingSubscriber
      ? editingSubscriber.email
      : newSubscriber.email;

    if (!validateEmail(emailToValidate)) {
      setError("Please enter a valid email address");
      return;
    }

    try {
      if (editingSubscriber) {
        await axios.put(
          `/api/newsletter/${editingSubscriber._id}`,
          { email: editingSubscriber.email },
          { withCredentials: true }
        );
        setEditingSubscriber(null);
      } else {
        await axios.post("/api/newsletter", newSubscriber, {
          withCredentials: true,
        });
        setNewSubscriber({ email: "" });
      }
      fetchSubscribers();
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "Operation failed");
    }
  };

  const handleEdit = (subscriber) => {
    setEditingSubscriber({ ...subscriber });
    setError("");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this subscriber?"))
      return;
    try {
      await axios.delete(`/api/newsletter/${id}`, { withCredentials: true });
      fetchSubscribers();
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete subscriber");
    }
  };

  const handleCancelEdit = () => {
    setEditingSubscriber(null);
    setError("");
  };

  // Filter subscribers based on search term
  const filteredSubscribers = subscribers.filter((subscriber) =>
    subscriber.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination logic
  const totalPages = Math.ceil(filteredSubscribers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentSubscribers = filteredSubscribers.slice(startIndex, endIndex);

  // Reset to page 1 when search term or items per page changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("...");
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <div className="max-w-[1280px] mx-auto p-4 md:p-8 bg-[#1a1a1a] rounded-lg text-white flex flex-col gap-4">
      <h2 className="text-xl text-[#d4a017] font-cinzel uppercase font-normal text-center md:text-left mb-4">
        {editingSubscriber ? "Edit Subscriber" : "Add New Subscriber"}
      </h2>
      {error && <p className="text-[#f44336] text-center mb-4">{error}</p>}

      <form onSubmit={handleSubmit} className="flex flex-col gap-4 mb-8">
        <input
          type="email"
          name="email"
          value={
            editingSubscriber ? editingSubscriber.email : newSubscriber.email
          }
          onChange={handleChange}
          placeholder="Email Address"
          className="p-2 bg-[#333] border border-[#555] rounded text-white"
          required
        />
        <div className="flex gap-2">
          <button
            type="submit"
            className="px-4 py-2 bg-[#d4a017] hover:bg-[#b38b00] text-white rounded transition-colors"
          >
            {editingSubscriber ? "Update Subscriber" : "Add Subscriber"}
          </button>
          {editingSubscriber && (
            <button
              type="button"
              onClick={handleCancelEdit}
              className="px-4 py-2 bg-[#666] hover:bg-[#555] text-white rounded transition-colors"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-4">
        <h3 className="text-lg text-[#d4a017] font-cinzel uppercase font-normal">
          Newsletter Subscribers ({filteredSubscribers.length})
        </h3>
        <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by email..."
            className="p-2 bg-[#333] border border-[#555] rounded text-white w-full md:w-64"
          />
          <select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            className="p-2 bg-[#333] border border-[#555] rounded text-white"
          >
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </select>
        </div>
      </div>

      {loading ? (
        <p className="text-[#ccc] text-center">Loading subscribers...</p>
      ) : filteredSubscribers.length === 0 ? (
        <p className="text-[#ccc] text-center">
          {searchTerm
            ? "No subscribers found matching your search"
            : "No subscribers available"}
        </p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <div className="text-[#999] text-sm mb-2">
              Showing {startIndex + 1}-
              {Math.min(endIndex, filteredSubscribers.length)} of{" "}
              {filteredSubscribers.length} subscribers
            </div>
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-[#444]">
                  <th className="text-left p-3 text-[#d4a017]">Email</th>
                  <th className="text-right p-3 text-[#d4a017]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentSubscribers.map((subscriber) => (
                  <tr
                    key={subscriber._id}
                    className="border-b border-[#333] hover:bg-[#222]"
                  >
                    <td className="p-3 text-white">{subscriber.email}</td>
                    <td className="p-3 text-right">
                      <div className="flex flex-row gap-2 justify-end">
                        <button
                          onClick={() => handleEdit(subscriber)}
                          className="px-3 py-1 bg-[#4CAF50] hover:bg-[#45a049] text-white text-sm rounded transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(subscriber._id)}
                          className="px-3 py-1 bg-[#f44336] hover:bg-[#da190b] text-white text-sm rounded transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex flex-col md:flex-row justify-center items-center gap-2 mt-6">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded transition-colors ${
                  currentPage === 1
                    ? "bg-[#333] text-[#666] cursor-not-allowed"
                    : "bg-[#555] text-white hover:bg-[#666]"
                }`}
              >
                Previous
              </button>

              <div className="flex gap-1">
                {getPageNumbers().map((page, index) =>
                  page === "..." ? (
                    <span
                      key={`ellipsis-${index}`}
                      className="px-3 py-1 text-[#999]"
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-1 rounded transition-colors ${
                        currentPage === page
                          ? "bg-[#d4a017] text-white"
                          : "bg-[#555] text-white hover:bg-[#666]"
                      }`}
                    >
                      {page}
                    </button>
                  )
                )}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 rounded transition-colors ${
                  currentPage === totalPages
                    ? "bg-[#333] text-[#666] cursor-not-allowed"
                    : "bg-[#555] text-white hover:bg-[#666]"
                }`}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default NewsletterTab;
